import { Controller } from "@hotwired/stimulus"

/**
 * Worksheet (theme-appraisal "Immersive Editorial")
 *
 * Turns an authored fill-in worksheet (a `.worksheet-form` of <fieldset>s with
 * <label> + <input> fields) into a usable, printable form. Mirrors the print
 * book's research worksheets (e.g. the Introduction's "State Licensing and
 * Certification Requirements"), which are meant to be filled in or printed
 * blank and written on.
 *
 *  - Persistence: field values are saved to localStorage keyed by
 *    data-worksheet-key-value and restored on load, so a student's research
 *    survives a page refresh. No backend, no auth — same in-the-moment spirit
 *    as the flip cards and quiz, just durable across reloads.
 *  - Print: the print() action prints ONLY the worksheet (scoped via a class on
 *    <html> + an @media print block), not the whole chapter, and hides the
 *    action buttons. Blank fields print as ruled lines to write on.
 *  - Clear: wipes the fields and the saved copy (with a confirm).
 *
 * Reusable across chapters: authors add the markup + data-controller="worksheet"
 * with a unique key; no per-chapter JS.
 *
 * Usage:
 *   <div class="worksheet-form" data-controller="worksheet"
 *        data-worksheet-key-value="intro-state-licensing"> … </div>
 */
export default class extends Controller {
  static targets = ["field"]
  static values = { key: String }

  connect() {
    this.#restore()
    this.element.addEventListener("input", this.#onInput)
  }

  disconnect() {
    this.element.removeEventListener("input", this.#onInput)
  }

  print() {
    // Clone the worksheet into a print-only portal at the top of <body> and let
    // the @media print rules display:none everything else. (Merely hiding the
    // rest with visibility:hidden still reserves its layout, printing ~20 blank
    // pages of the chapter below.) cloneNode doesn't carry live input values, so
    // copy them onto the clone; strip ids/controller so nothing collides.
    const clone = this.element.cloneNode(true)
    clone.removeAttribute("data-controller")
    const live = this.element.querySelectorAll("input")
    clone.querySelectorAll("input").forEach((input, i) => {
      if (live[i]) input.setAttribute("value", live[i].value)
      input.removeAttribute("id")
      input.removeAttribute("data-worksheet-target")
    })

    const portal = document.createElement("div")
    portal.className = "worksheet-print-portal"
    portal.appendChild(clone)
    document.body.appendChild(portal)

    const root = document.documentElement
    root.classList.add("worksheet-printing")
    const cleanup = () => {
      root.classList.remove("worksheet-printing")
      portal.remove()
      window.removeEventListener("afterprint", cleanup)
    }
    window.addEventListener("afterprint", cleanup)
    window.print()
  }

  clear() {
    if (!window.confirm("Clear all your answers on this worksheet?")) return
    this.fieldTargets.forEach((field) => (field.value = ""))
    this.#persist()
  }

  #onInput = () => this.#persist()

  get #storageKey() {
    return `appraisal-worksheet:${this.keyValue}`
  }

  #persist() {
    const data = {}
    this.fieldTargets.forEach((field) => {
      if (field.id) data[field.id] = field.value
    })
    try {
      window.localStorage.setItem(this.#storageKey, JSON.stringify(data))
    } catch (e) {
      /* storage unavailable (private mode / disabled) — fields still work, just not durable */
    }
  }

  #restore() {
    let data = {}
    try {
      data = JSON.parse(window.localStorage.getItem(this.#storageKey) || "{}")
    } catch (e) {
      data = {}
    }
    this.fieldTargets.forEach((field) => {
      if (field.id && data[field.id] != null) field.value = data[field.id]
    })
  }
}
